import {
    useCallback, useEffect, useMemo,
    useState,
} from 'react'
import isNil from 'lodash/isNil'

import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import useRequest, {
    RequestKeys,
    RequestFn,
} from 'app/hooks/useRequest'
import {
    RequestConfig, TableResponse,
} from 'app/types/request.types'

import {
    SkyNetPaginationType,
} from 'app/shared-components/SkyNetPagination'
import useRichtableLoadWithSpinner from './useRichtableLoadWithSpinner'

type Params = {
    config: (args: Record<string, any>) => RequestConfig,
    refetchInterval?: number | false | ((any) => number | false),
    key: RequestKeys,
    params: Record<string, any>,
    defaultValue?: any,
    keepPreviousData?: boolean,
    showArchived?: boolean,
    enabled?: boolean,
    withPagination?: boolean,
    paginationOptions?: SkyNetPaginationType,
}

const useRequestTable = <DataType>({
    refetchInterval,
    defaultValue,
    config,
    params,
    key,
    keepPreviousData,
    showArchived,
    enabled = true,
    withPagination = false,
    paginationOptions,
}: Params) => {
    const [
        requestParams,
        setParams,
    ] = useState(() => {
        return defaultValue || {
            typeMapping: {},
        }
    })

    // TODO: discuss why we need typeMapping
    const [defaultFilter] = useState(defaultValue?.filter)

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        refetch,
        invalidate,
    } = useRequest<DataType>({
        key: RequestKeys[key],
        params: {
            ...params,
            params: {
                data: {
                    ...withPagination ? {
                        paginationModeMatches: true,
                        rows: paginationOptions?.rows,
                        start: paginationOptions?.start,
                    } : {},
                },
                ...requestParams,
                archived: showArchived,
            },
        },
        requestFunc: RequestFn.table(config, {
            withPagination,
        }),
        refetchInterval,
        keepPreviousData,
        enabled,
    })

    const onParamsChange = useCallback((newParamsRequest) => {
        setParams((prevState) => {
            // discard pagination to first page when filter is changed
            if (withPagination
                && !isEmpty(newParamsRequest.filter)
                && !isEqual(prevState.filter, newParamsRequest.filter)) {
                paginationOptions?.onChange(null, 1)
            }
            let newState = {
                ...prevState,
                ...newParamsRequest,
                filter: {
                    ...prevState.filter,
                    ...newParamsRequest.filter,
                },
            }

            if (prevState?.filter && isNil(newParamsRequest?.filter)) {
                newState = {
                    ...newState,
                    filter: defaultFilter || undefined,
                }
            }

            if (newParamsRequest?.filter && defaultFilter) {
                newState = {
                    ...newState,
                    filter: {
                        ...defaultFilter,
                        ...newParamsRequest.filter,
                    },
                }
            }

            return newState
        })
    }, [
        defaultFilter,
        paginationOptions,
        withPagination,
    ])

    const pageData = useMemo(() => {
        return {
            ...data,
            ...withPagination ? {
                meta: {
                    matchedResults: 0,
                },
                hasNextPage: false,
            } : undefined,
        }
    }, [
        data,
        withPagination,
    ])

    const loadData = useRichtableLoadWithSpinner<DataType>({
        data: pageData,
        isDataReady,
    })

    useEffect(() => {
        if (isDataReady && withPagination) {
            paginationOptions?.setMatches((data as unknown as TableResponse<DataType>)?.matches)
        }
    }, [
        data,
        isDataReady,
        paginationOptions,
        withPagination,
    ])

    return {
        onParamsChange,
        isDataReady,
        isFetching,
        isError,
        error,
        refetch,
        data,
        loadData,
        invalidate,
    }
}

export default useRequestTable
